// SortContext.tsx
import React, { createContext, useContext, useEffect, useState } from 'react';
import { usePage } from './table-page';
import { useSearchParams } from 'react-router-dom';

type SortDirection = 'asc' | 'desc';
type SortColumn = 'OrderDate' | 'SalesAmount' | 'OrderCount';

interface SortState {
  sortColumn: SortColumn;
  sortDirection: SortDirection;
}

interface SortContextValue {
  sort: SortState | null;
  setSort: (column: SortColumn) => void;
  resetSort: () => void;
}

const SortContext = createContext<SortContextValue | undefined>(undefined);

export const SortProvider = ({ children }: { children: React.ReactNode }) => {
  const [sort, setSortState] = useState<SortState | null>(null);
  const [searchParams] = useSearchParams();
  const orderTab = searchParams.get('orderTab');

  const { resetPage } = usePage();

  const setSort = (column: SortColumn) => {
    resetPage();
    setSortState((prev) => {
      // 같은 컬럼 클릭 시 asc → desc → null 순환
      if (!prev) return { sortColumn: column, sortDirection: 'asc' };

      if (prev.sortColumn === column) {
        if (prev.sortDirection === 'asc') return { sortColumn: column, sortDirection: 'desc' };
        if (prev.sortDirection === 'desc') return null;
      }
      return { sortColumn: column, sortDirection: 'asc' };
    });
  };

  const resetSort = () => {
    resetPage();
    setSortState(null);
  };

  useEffect(() => {
    resetSort();
  }, [orderTab]);

  return (
    <SortContext.Provider value={{ sort, setSort, resetSort }}>{children}</SortContext.Provider>
  );
};

export const useSort = () => {
  const context = useContext(SortContext);
  if (!context) {
    throw new Error('useSort must be used within a SortProvider');
  }
  return context;
};
